import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, API_BASE } from './client';
import { clearToken } from '@/lib/authToken';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: 'platform_admin' | 'sre' | 'support' | 'billing' | 'read_only';
  status: 'active' | 'suspended';
  picture?: string | null;
  created_at?: string;
  last_seen_at?: string | null;
}

export interface AuthConfig {
  google_oauth_enabled: boolean;
  allowed_domains: string[];
}

export function useMe() {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => apiFetch<CurrentUser>('/auth/me'),
    staleTime: 60_000,
    retry: false,
    // Shared between AuthGuard and the app shell. Without this, a shell
    // component mounting while the query is errored resets it to pending,
    // AuthGuard swaps back to its spinner, the shell unmounts, and the
    // cycle repeats at render speed (FH-009). Retries are driven
    // explicitly by AuthGuard's interval instead.
    retryOnMount: false,
  });
}

export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth', 'config'],
    queryFn: () => apiFetch<AuthConfig>('/auth/config'),
    staleTime: Infinity,
    retry: false,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await apiFetch('/auth/logout', { method: 'POST' });
      } catch {
        /* server-side logout is best-effort */
      }
      clearToken();
    },
    onSuccess: () => {
      qc.clear();
    },
  });
}

/** Full URL the user should be sent to in order to start Google OAuth. */
export function googleLoginUrl(): string {
  return `${API_BASE}/auth/google/login`;
}
